import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { getTodayDateString } from '@/lib/utils';
import { useCreateJobCharge } from '../hooks/useCreateJobCharge';
import { useUpdateJobCharge } from '../hooks/useUpdateJobCharge';
import { ChargeImagesSection } from './ChargeImagesSection';
import type { JobCharge } from '../types/jobCharge';

interface ChargeEntryFormProps {
  jobId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingCharge?: JobCharge | null;
  isForecast: boolean;
}

export function ChargeEntryForm({
  jobId,
  isOpen,
  onClose,
  onSuccess,
  editingCharge,
  isForecast,
}: ChargeEntryFormProps) {
  const { create, isCreating, error: createError } = useCreateJobCharge();
  const { update, isUpdating, error: updateError } = useUpdateJobCharge();

  // Form state
  const [chargeDate, setChargeDate] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState('');

  const error = createError || updateError || validationError;
  const isSubmitting = isCreating || isUpdating;

  // Initialize form when dialog opens or editingCharge changes
  useEffect(() => {
    if (isOpen) {
      if (editingCharge) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setChargeDate(editingCharge.chargeDate.split('T')[0]);
        setAmount(editingCharge.amount.toString());
        setDescription(editingCharge.description || '');
      } else {
        setChargeDate(getTodayDateString());
        setAmount('');
        setDescription('');
      }
      setValidationError('');
    }
  }, [isOpen, editingCharge]);

  const validateForm = (): boolean => {
    if (!chargeDate) {
      setValidationError('Charge date is required');
      return false;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      setValidationError('Amount must be a valid number');
      return false;
    }

    if (amountNum === 0) {
      setValidationError('Amount cannot be zero');
      return false;
    }

    if (description && description.length > 200) {
      setValidationError('Description must be 200 characters or less');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!validateForm()) {
      return;
    }

    const data = {
      isForecast,
      chargeDate: new Date(chargeDate).toISOString(),
      amount: parseFloat(amount),
      description: description.trim() || undefined,
    };

    let result;
    if (editingCharge) {
      result = await update(jobId, editingCharge.id, data);
    } else {
      result = await create(jobId, data);
    }

    if (result) {
      onSuccess();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingCharge
              ? 'Edit Charge'
              : `Add ${isForecast ? 'Forecasted' : 'Actual'} Charge`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <ErrorAlert error={error} />

          <div className="space-y-2">
            <Label htmlFor="chargeDate">
              Charge Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="chargeDate"
              type="date"
              value={chargeDate}
              onChange={(e) => setChargeDate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">
              Amount <span className="text-red-500">*</span>
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <p className="text-xs text-gray-500">
              Use a negative amount for credits or reductions
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              type="text"
              placeholder="e.g., Original Contract, Change Order #1"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
            />
            <p className="text-xs text-gray-500">
              {description.length} / 200 characters
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? 'Saving...'
                : editingCharge
                  ? 'Update Charge'
                  : 'Add Charge'}
            </Button>
          </div>

          {editingCharge && (
            <div className="border-t border-gray-200 my-4 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Contract Documents
              </h3>
              <ChargeImagesSection
                jobId={jobId}
                chargeId={editingCharge.id}
                canDelete={true}
              />
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
